import { useState } from 'react';

/**
 * Shared React components for the portal surfaces.
 *
 * These wrap the classes in base.css so a screen never hand-writes markup that
 * carries an accessibility obligation. `Field` is the clearest example: every
 * input needs a label association, an error announced to assistive technology,
 * and `aria-invalid` — easy to get right once here, easy to forget per screen.
 *
 * Plain JavaScript with JSDoc, per specs.md §1a.
 */

/**
 * Brand lockup.
 * @param {{ href?: string, label?: string }} props
 */
export function Brand({ href = '/', label }) {
  return (
    <a className="brand" href={href}>
      <BrandMark />
      <span>{label}</span>
    </a>
  );
}

/**
 * The msarat.ai mark: three rising paths with waypoints, the same artwork the
 * marketing site and the favicon use. Decorative here — the wordmark beside it
 * carries the name, so it is hidden from assistive technology rather than
 * announced twice.
 */
export function BrandMark() {
  return (
    <span className="brand__mark" aria-hidden="true">
      <svg viewBox="0 0 48 48" focusable="false">
        <path d="M9 35c4-12 10-20 19-24" />
        <path d="M16 38c5-10 11-16 22-21" />
        <circle cx="11" cy="36" r="3" />
        <circle cx="29" cy="10" r="3" />
        <circle cx="39" cy="17" r="3" />
      </svg>
    </span>
  );
}

/**
 * A labelled form control with hint and error wiring.
 *
 * The error is rendered in a `role="alert"` region and referenced from the
 * input via aria-describedby, so a screen reader announces it when it appears
 * instead of leaving the user with a silently rejected form.
 *
 * @param {{
 *   id: string,
 *   label: string,
 *   type?: string,
 *   value: string,
 *   onChange: (value: string) => void,
 *   error?: string,
 *   hint?: string,
 *   required?: boolean,
 *   autoComplete?: string,
 *   inputMode?: string,
 *   ltr?: boolean,
 *   maxLength?: number,
 *   children?: unknown,
 * }} props
 */
export function Field({
  id,
  label,
  type = 'text',
  value,
  onChange,
  error,
  hint,
  required = false,
  autoComplete,
  inputMode,
  ltr = false,
  maxLength,
  optionalLabel,
  children,
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required || !optionalLabel ? null : <span className="field__hint"> {optionalLabel}</span>}
      </label>

      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}

      {children ?? (
        <input
          className={`input${ltr ? ' input--ltr' : ''}`}
          id={id}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
        />
      )}

      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Submit button that shows a pending state and cannot be double-submitted.
 * @param {{ pending?: boolean, children: unknown, pendingLabel?: string }} props
 */
export function SubmitButton({ pending = false, children, pendingLabel = 'جارٍ الإرسال…' }) {
  return (
    <button className="button button--primary button--block" type="submit" disabled={pending}>
      {pending ? pendingLabel : children}
    </button>
  );
}

/**
 * Form-level message.
 *
 * `role="alert"` on errors so failures are announced; successes use
 * `role="status"`, which does not interrupt what a screen reader is saying.
 *
 * @param {{ kind?: 'error'|'success', children: unknown }} props
 */
export function Alert({ kind = 'error', children }) {
  if (!children) return null;
  return (
    <p className={`alert alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  );
}

/**
 * Mastery status pill.
 *
 * The label is part of the component, never colour alone: distinguishing
 * "mastered" from "assisted" is the product's core claim, and it has to survive
 * colour blindness and greyscale printing.
 *
 * @param {{ kind: 'mastered'|'assisted'|'review' }} props
 */
export function StatusPill({ kind, label }) {
  // The label is passed in rather than held here: this package must not contain
  // interface strings, or it would need its own translation table.
  return <span className={`status status--${kind}`}>{label ?? kind}</span>;
}

/**
 * Centred single-card page used by every authentication screen.
 * @param {{ title: string, subtitle?: string, children: unknown, footer?: unknown }} props
 */
export function AuthLayout({ title, subtitle, children, footer, brandLabel }) {
  return (
    <div className="center-page">
      <main className="card card--auth stack">
        <Brand label={brandLabel} />
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <h1>{title}</h1>
          {subtitle ? <p style={{ color: 'var(--color-ink-muted)' }}>{subtitle}</p> : null}
        </div>
        {children}
        {footer ? <div className="field__hint">{footer}</div> : null}
      </main>
    </div>
  );
}

/**
 * Password input with a show/hide toggle.
 *
 * The toggle is a real button, not an icon with a click handler: it must be
 * reachable by keyboard and announced, since someone who cannot see the field
 * is exactly who benefits most from checking what they typed. `aria-pressed`
 * carries the state, and the label changes with it so a screen reader
 * announces the action rather than a static "show password".
 *
 * type="button" matters too — inside a form, a bare <button> submits.
 *
 * @param {{
 *   id: string,
 *   label: string,
 *   value: string,
 *   onChange: (value: string) => void,
 *   error?: string,
 *   hint?: string,
 *   required?: boolean,
 *   autoComplete?: string,
 * }} props
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  required = false,
  autoComplete = 'current-password',
  showLabel = 'Show password',
  hideLabel = 'Hide password',
}) {
  const [visible, setVisible] = useState(false);
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>

      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}

      <div className="password">
        <input
          className="input input--ltr password__input"
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          autoComplete={autoComplete}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
        />
        <button
          className="password__toggle"
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-pressed={visible}
          aria-controls={id}
          aria-label={visible ? hideLabel : showLabel}
          title={visible ? hideLabel : showLabel}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** Decorative only: the button carries the accessible name. */
function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"
         fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"
         fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M2.5 12S6 5.5 12 5.5c1.7 0 3.2.5 4.5 1.2M21.5 12S18 18.5 12 18.5c-1.7 0-3.2-.5-4.5-1.2" />
      <path d="M9.9 9.9a3.2 3.2 0 0 0 4.2 4.2" />
      <path d="M4 4l16 16" />
    </svg>
  );
}
